import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, mergeMap, catchError, withLatestFrom, filter, switchMap } from 'rxjs/operators';
import { ParameterService } from '../services/parameter.service';
import { LoggerService } from '../../../core/services/logger.service';
import * as ParameterActions from './parameter.actions';
import * as ParameterSelectors from './parameter.selectors';
import { ParameterDetail } from '../models/parameter.interface';

@Injectable()
export class ParameterEffects {
  private actions$ = inject(Actions);
  private parameterService = inject(ParameterService);
  private store = inject(Store);
  private logger = inject(LoggerService);

  // Intelligent cache-aware effects
  loadAllParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParameterActions.loadAllParameters),
      withLatestFrom(this.store.select(ParameterSelectors.selectShouldFetchData)),
      filter(([, shouldFetch]) => shouldFetch),
      switchMap(() =>
        this.parameterService.getAllParameters().pipe(
          map(parameters => ParameterActions.loadAllParametersSuccess({ parameters })),
          catchError(error =>
            of(ParameterActions.loadAllParametersFailure({ error: error.message }))
          )
        )
      )
    )
  );

  createParameter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParameterActions.createParameter),
      mergeMap(({ request }) =>
        this.parameterService.createParameter(request).pipe(
          map(parameter => ParameterActions.createParameterSuccess({ parameter })),
          catchError(error => of(ParameterActions.createParameterFailure({ error: error.message })))
        )
      )
    )
  );

  // Reload parameters after successful creation to ensure dropdown is updated
  reloadParametersAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParameterActions.createParameterSuccess),
      map(() => ParameterActions.loadAllParameters())
    )
  );

  updateParameter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParameterActions.updateParameter),
      mergeMap(({ id, request }) =>
        this.parameterService.updateParameter(id, request).pipe(
          map(parameter => ParameterActions.updateParameterSuccess({ parameter })),
          catchError(error => of(ParameterActions.updateParameterFailure({ error: error.message })))
        )
      )
    )
  );

  fetchParameterById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParameterActions.fetchParameterById),
      mergeMap(({ id }) =>
        this.parameterService.getParameterById(id).pipe(
          map(parameter => ParameterActions.fetchParameterByIdSuccess({ parameter })),
          catchError(error =>
            of(ParameterActions.fetchParameterByIdFailure({ error: error.message }))
          )
        )
      )
    )
  );

  loadActiveParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParameterActions.loadActiveParameters),
      withLatestFrom(this.store.select(ParameterSelectors.selectShouldFetchData)),
      filter(([, shouldFetch]) => shouldFetch),
      switchMap(() =>
        this.parameterService.getActiveParameters().pipe(
          map(parameters => ParameterActions.loadActiveParametersSuccess({ parameters })),
          catchError(error =>
            of(ParameterActions.loadActiveParametersFailure({ error: error.message }))
          )
        )
      )
    )
  );

  loadInactiveParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParameterActions.loadInactiveParameters),
      withLatestFrom(this.store.select(ParameterSelectors.selectShouldFetchData)),
      filter(([, shouldFetch]) => shouldFetch),
      switchMap(() =>
        this.parameterService.getInactiveParameters().pipe(
          map(parameters => ParameterActions.loadInactiveParametersSuccess({ parameters })),
          catchError(error =>
            of(ParameterActions.loadInactiveParametersFailure({ error: error.message }))
          )
        )
      )
    )
  );

  // Parameter Detail effects
  createParameterDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParameterActions.createParameterDetail),
      withLatestFrom(this.store.select(ParameterSelectors.selectAllParameters)),
      mergeMap(([{ parameterId, detail }, parameters]) => {
        const parameter = parameters.find(p => p.idParametro === parameterId);
        if (!parameter) {
          return of(
            ParameterActions.createParameterDetailFailure({
              error: 'Parameter not found'
            })
          );
        }

        // Create new detail locally since API doesn't support it
        const newDetail: ParameterDetail = {
          idParametroDetalle: Date.now(), // Generate unique ID
          idPadre: null,
          parametroId: parameterId,
          vcNombre: detail.vcNombre || '',
          txDescripcion: detail.txDescripcion || '',
          vcCodigoInterno: detail.vcCodigoInterno || '',
          dCodigoIterno: detail.dCodigoIterno || 0,
          bEstado: detail.bEstado ?? true,
          rangoDesde: detail.rangoDesde || 0,
          rangoHasta: detail.rangoHasta || 0
        };

        return of(
          ParameterActions.createParameterDetailSuccess({
            parameterId,
            detail: newDetail
          })
        );
      }),
      catchError(error =>
        of(
          ParameterActions.createParameterDetailFailure({
            error: error.message
          })
        )
      )
    )
  );

  updateParameterDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParameterActions.updateParameterDetail),
      withLatestFrom(this.store.select(ParameterSelectors.selectAllParameters)),
      mergeMap(([{ parameterId, detailId, detail }, parameters]) => {
        const parameter = parameters.find(p => p.idParametro === parameterId);
        if (!parameter) {
          return of(
            ParameterActions.updateParameterDetailFailure({
              error: 'Parameter not found'
            })
          );
        }

        const existingDetail = parameter.parametroDetalles?.find(
          d => d.idParametroDetalle === detailId
        );
        if (!existingDetail) {
          return of(
            ParameterActions.updateParameterDetailFailure({
              error: 'Parameter detail not found'
            })
          );
        }

        // Update detail locally since API doesn't support it
        const updatedDetail: ParameterDetail = {
          ...existingDetail,
          ...detail
        };

        return of(
          ParameterActions.updateParameterDetailSuccess({
            parameterId,
            detail: updatedDetail
          })
        );
      }),
      catchError(error =>
        of(
          ParameterActions.updateParameterDetailFailure({
            error: error.message
          })
        )
      )
    )
  );
}
